import { useQuery } from "@apollo/client";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Alert, AlertTitle, Box, Collapse, Link, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { gql } from "generated-graphql";
import { WasteRequirementReadinessIssuesQuery } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { usePaginationModel } from "hooks/usePaginationModel";
import { useTenant } from "hooks/useTenant";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

const WASTE_REQUIREMENT_READINESS_ISSUES_QUERY = gql(`
  query WasteRequirementReadinessIssues($tenantId: ID!, $page: Int, $pageSize: Int) {
    wasteRequirementReadinessIssues(tenantId: $tenantId, page: $page, pageSize: $pageSize) {
      count
      facilityIds
      items {
        facilityId
        facilityName
        customerFacilityId
        state
        county
        message
        propertyKey
        issueId
      }
    }
  }
`);

const EDITABLE_INSTRUCTIONS =
  "Update the County field in the facility profile. After saving the change, allow up to 24 hours for this warning to disappear.";

const READ_ONLY_INSTRUCTIONS =
  "Contact a facility administrator to update the County field. After the change is saved, allow up to 24 hours for this warning to disappear.";

type ReadinessIssueRow =
  WasteRequirementReadinessIssuesQuery["wasteRequirementReadinessIssues"]["items"][number];

function readinessIssueColumns({
  profilePath,
  canEditFacility,
}: {
  profilePath: (facilityId: string) => string;
  canEditFacility: (facilityId: string) => boolean;
}): CustomGridColDef<ReadinessIssueRow>[] {
  return [
    {
      field: "facilityName",
      headerName: "Facility",
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Link
          component={RouterLink}
          to={profilePath(row.facilityId)}
          variant="body2"
          sx={{ textAlign: "left" }}
        >
          {row.facilityName}
        </Link>
      ),
    },
    {
      field: "customerFacilityId",
      headerName: "Facility ID",
      width: 120,
    },
    {
      field: "state",
      headerName: "State/Territory",
      width: 72,
    },
    {
      field: "county",
      headerName: "County",
      width: 140,
      valueFormatter: (value) => value ?? "—",
    },
    {
      field: "message",
      headerName: "Issue",
      flex: 2,
      minWidth: 280,
    },
    {
      field: "actions",
      headerName: "",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        if (!canEditFacility(row.facilityId)) {
          return null;
        }
        return (
          <Link
            component={RouterLink}
            to={profilePath(row.facilityId)}
            variant="body2"
            // Rows are issues, not facilities, so the facility name alone can
            // repeat. The issue text keeps each link's name distinct.
            aria-label={`Update county for ${row.facilityName}: ${row.message}`}
          >
            Update county
          </Link>
        );
      },
    },
  ];
}

export function RequirementReadinessIssuesSection() {
  const { tenantId } = useTenant();
  const { hasWritePermissionForFacility, loading: loadingAuthorization } = useAuthorization();
  const [expanded, setExpanded] = useState(true);
  const [paginationModel, setPaginationModel] = usePaginationModel(5);

  const { data, previousData, loading } = useQuery(WASTE_REQUIREMENT_READINESS_ISSUES_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    },
    skip: !tenantId,
  });

  const result =
    data?.wasteRequirementReadinessIssues ?? previousData?.wasteRequirementReadinessIssues;
  // `count` is the issue-row count the grid pages through. The warning talks about
  // facilities, and a facility can carry two county issues, so it counts
  // `facilityIds` instead.
  const count = result?.count ?? 0;
  const facilityIds = result?.facilityIds ?? [];

  // Waiting on permissions instead of rendering the read-only copy first keeps the
  // warning from flipping its instructions a moment after it appears.
  if (count === 0 || loadingAuthorization) {
    return null;
  }

  const items = result?.items ?? [];

  const profilePath = (facilityId: string) => `/o/${tenantId}/facilities/${facilityId}/profile`;
  // Every affected facility, not just the page on screen, so the instructions do
  // not change as the reader pages through the grid and do not tell a reader to
  // edit facilities they have no write access to.
  const editableFacilityIds = facilityIds.filter(hasWritePermissionForFacility);
  const singleFacility =
    facilityIds.length === 1 && editableFacilityIds.length === 1 ? items[0] : undefined;

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="warning"
        icon={<WarningAmber />}
        action={
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setExpanded((value) => !value)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {expanded ? "Hide details" : "Show details"}
          </Link>
        }
      >
        <AlertTitle>
          {facilityIds.length} {facilityIds.length === 1 ? "facility needs" : "facilities need"} a
          valid county before waste submissions can be scheduled
        </AlertTitle>
        <Typography variant="body2">
          {editableFacilityIds.length > 0 ? EDITABLE_INSTRUCTIONS : READ_ONLY_INSTRUCTIONS}
        </Typography>
        {singleFacility && (
          <Link
            component={RouterLink}
            to={profilePath(singleFacility.facilityId)}
            variant="body2"
            sx={{ display: "inline-block", mt: 1, fontWeight: 600 }}
          >
            Update facility county
          </Link>
        )}
      </Alert>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          <DataGrid
            columns={readinessIssueColumns({
              profilePath,
              canEditFacility: hasWritePermissionForFacility,
            })}
            rows={items}
            getRowId={(row) => `${row.facilityId}-${row.issueId}`}
            rowCount={count}
            loading={loading}
            noRowsMessage="No readiness issues"
            isRowSelectable={() => false}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25]}
            autoHeight
            disableRowSelectionOnClick
          />
        </Box>
      </Collapse>
    </Box>
  );
}
